import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageBanner from "../components/PageBanner";
import SectionIntro from "../components/SectionIntro";
import api from "../lib/api";
import SEO from "../lib/SEO";
import { useSettings } from "../lib/SettingsContext";
import SmartImage from "../components/SmartImage";

const FALLBACK_BANNER = "https://images.unsplash.com/photo-1602663491496-73f07481dbea?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

function FlavourCard({ f, i }) {
    return (
        <Link
            to={f.slug ? `/flavours/${f.slug}` : "#"}
            className="card-lift bg-white border border-[#EAE3DE] block group"
            data-testid={`flavour-card-${i}`}
        >
            <div className="aspect-square overflow-hidden bg-blush-pale flex items-center justify-center">
                {f.image_url ? (
                    <SmartImage
                        src={f.image_url}
                        alt={f.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                    />
                ) : (
                    <span className="text-xs tracking-[0.3em] uppercase text-blush/60">Image coming soon</span>
                )}
            </div>
            <div className="p-4">
                <h3 className="font-display text-lg text-ink mb-1">{f.name}</h3>
                <p className="text-sm text-ink-soft leading-snug">
                    {f.description}
                </p>
            </div>
        </Link>
    );
}

function FlavoursGrid({ items }) {
    return (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 md:gap-3">
            {items.map((f, i) => (
                <FlavourCard key={f.id} f={f} i={i} />
            ))}
        </div>
    );
}

export default function Flavours() {
    const { settings } = useSettings();
    const [flavours, setFlavours] = useState([]);
    const [loaded, setLoaded] = useState(false);
    useEffect(() => {
        api.get("/flavours")
            .then((r) => setFlavours(Array.isArray(r.data) ? r.data : []))
            .catch(() => {})
            .finally(() => setLoaded(true));
    }, []);

    const { standard, gourmet } = useMemo(() => {
        const s = flavours.filter((f) => (f.section || "standard") === "standard");
        const g = flavours.filter((f) => f.section === "gourmet");
        return { standard: s, gourmet: g };
    }, [flavours]);

    return (
        <main data-testid="flavours-page">
            <SEO
                title="Flavours — Cake Central"
                description="Explore Cake Central's cake flavours — Standard and Gourmet selections, hand-crafted in our Sydney studio."
                path="/flavours"
            />
            <PageBanner
                eyebrow="Tasting menu"
                title="Our Flavours"
                image={settings?.banner_flavours || FALLBACK_BANNER}
            />

            <section className="bg-[#FFF7FB] py-12 md:py-16">
                <div className="px-4 sm:px-6 lg:px-10 max-w-screen-2xl mx-auto">
                    <SectionIntro className="mb-14">
                        Every flavour is <em>hand-crafted</em> in our Sydney studio — mix and match across
                        tiers, because the best cakes are <em>never just one taste</em>.
                    </SectionIntro>

                    {/* ── Standard Flavours ────────────────────────────────────────── */}
                    {standard.length > 0 && (
                        <div className="mb-16 md:mb-24">
                            <div className="text-center mb-8 md:mb-10">
                                <p className="text-xs tracking-[0.45em] uppercase text-blush mb-2 font-medium">Everyday favourites</p>
                                <h2 className="font-display text-3xl md:text-5xl text-ink leading-none">
                                    Standard <span className="italic text-blush">Flavours</span>
                                </h2>
                                <div className="h-[2px] w-14 bg-blush mx-auto mt-5" />
                            </div>
                            <FlavoursGrid items={standard} />
                        </div>
                    )}

                    {/* ── Gourmet Flavours ─────────────────────────────────────────── */}
                    {gourmet.length > 0 && (
                        <div>
                            <div className="text-center mb-8 md:mb-10">
                                <p className="text-xs tracking-[0.45em] uppercase text-blush mb-2 font-medium">Signature creations</p>
                                <h2 className="font-display text-3xl md:text-5xl text-ink leading-none">
                                    Gourmet <span className="italic text-blush">Flavours</span>
                                </h2>
                                <div className="h-[2px] w-14 bg-blush mx-auto mt-5" />
                            </div>
                            <FlavoursGrid items={gourmet} />
                        </div>
                    )}

                    {loaded && flavours.length === 0 && (
                        <p className="text-center text-ink-soft py-20">Flavours coming soon.</p>
                    )}
                </div>
            </section>
        </main>
    );
}
